import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useAuth } from '../hooks/useAuth';
import { PageContainer } from '../components/PageContainer';
import { EventTimeDisplay } from '../components/EventTimeDisplay';
import { ImageModal } from '../components/ui/ImageModal';
import { ImgWithSkeleton } from '../components/ui/ImgWithSkeleton';
import { ReportErrorLink } from '../components/ReportErrorLink';
import { UserRole } from '@wineryxplorer/shared';
import type { Media } from '@wineryxplorer/shared';
import {
  EventDetailHeader,
  DeleteEventModal,
  EventWhereSection,
  EventContactInfo,
  EventCallsToAction,
  EventReviews,
} from '../components/EventDetail';
import type { EventWithVenue } from '../components/EventDetail';

export const EventDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  const { data: eventData, isLoading, error } = trpc.event.getById.useQuery(
    { id: id || '' },
    { enabled: !!id }
  );

  // Type the event properly
  const event = eventData as EventWithVenue | undefined;

  // Extract venueId as string for queries
  const venueIdString =
    event && typeof event.venueId === 'string'
      ? event.venueId
      : event && typeof event.venueId === 'object'
        ? (event.venueId as unknown as { _id?: string; id?: string })._id ||
          (event.venueId as unknown as { _id?: string; id?: string }).id
        : undefined;

  // Fetch media for the event's venue
  const { data: venueMedia } = trpc.media.listByEntity.useQuery(
    { entityType: 'venue', entityId: venueIdString || '' },
    { enabled: Boolean(venueIdString) }
  );

  // Delete mutation
  const deleteEvent = trpc.event.delete.useMutation({
    onSuccess: () => {
      // Redirect to events list on successful delete
      navigate('/events');
    },
    onError: (error) => {
      alert(`Error deleting event: ${error.message}`);
    },
  });

  // Cast media array to proper type
  const mediaList = (venueMedia?.items ?? []) as Media[];

  // Get event photos for gallery
  const getEventPhotos = (): Media[] => {
    if (!mediaList.length || !event?.photoIds.length) {
      return [];
    }
    return mediaList.filter((media) => event.photoIds.includes(media.id));
  };

  const handleImageClick = (imageIndex: number) => {
    setSelectedImageIndex(imageIndex);
    setModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer>
          <div className="text-center py-8">
            <p className="text-gray-600">Loading event...</p>
          </div>
        </PageContainer>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
        <PageContainer>
          <div className="text-center py-8">
            <p className="text-red-600">
              {error?.message || 'Event not found'}
            </p>
          </div>
        </PageContainer>
      </div>
    );
  }

  // Check if user has permission to edit/delete
  const canManage =
    user &&
    (user.role === UserRole.SUPER_ADMIN ||
      user.role === UserRole.SITE_ADMIN ||
      user.id === event.organizerId);

  const isAtCapacity = event.maxAttendees
    ? event.currentAttendees >= event.maxAttendees
    : false;

  const handleDelete = async () => {
    if (!id) return;
    await deleteEvent.mutateAsync({ id });
  };

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-4xl mx-auto">
      <PageContainer>
        {/* Header with badges and actions */}
        <EventDetailHeader
          event={event}
          isAtCapacity={isAtCapacity}
          canManage={!!canManage}
          isDeleting={deleteEvent.isPending}
          onEdit={() => navigate(`/events/${id}/edit`)}
          onRequestDelete={() => setShowDeleteModal(true)}
        />

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <DeleteEventModal
            eventName={event.name}
            isDeleting={deleteEvent.isPending}
            onCancel={() => setShowDeleteModal(false)}
            onConfirm={handleDelete}
          />
        )}

        {/* Event Details */}
        <div className="space-y-6">
          {/* Date and Time */}
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">When</h2>
            <EventTimeDisplay event={event} />
          </div>

          {/* Venue information, or the free-form location for standalone events */}
          <EventWhereSection event={event} venueIdString={venueIdString} />

          {/* Description */}
          {event.description && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">About</h2>
              <p className="text-gray-700 whitespace-pre-wrap">{event.description}</p>
            </div>
          )}

          {/* Capacity Information */}
          {event.maxAttendees && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Capacity</h2>
              <div className="flex items-center">
                <div className="flex-1">
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-gray-600">
                      {event.currentAttendees} / {event.maxAttendees} attendees
                    </span>
                    <span className="text-gray-600">
                      {Math.round((event.currentAttendees / event.maxAttendees) * 100)}% full
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all ${
                        isAtCapacity ? 'bg-red-500' : 'bg-green-500'
                      }`}
                      style={{
                        width: `${Math.min(
                          (event.currentAttendees / event.maxAttendees) * 100,
                          100
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Price */}
          {event.price?.amount !== undefined && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Price</h2>
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span className="text-lg font-medium text-gray-900">
                  ${event.price.amount.toFixed(2)} {event.price.currency}
                </span>
              </div>
            </div>
          )}

          {/* Contact Information */}
          <EventContactInfo contactInfo={event.contactInfo} />

          {/* Tags */}
          {event.tags.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Tags</h2>
              <div className="flex flex-wrap gap-2">
                {event.tags.map((tag: string) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Gallery Images */}
          {event.venueId && event.photoIds.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-3">Gallery</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {getEventPhotos()
                  .slice(0, 6)
                  .map((media, index) => (
                    <div
                      key={media.id}
                      className="aspect-square overflow-hidden rounded-lg cursor-pointer"
                      onClick={() => handleImageClick(index)}
                    >
                      <ImgWithSkeleton
                        src={media.cloudinaryUrl}
                        alt={media.caption || 'Event image'}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-200"
                        skeletonClassName="rounded-lg"
                      />
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Members-only pitch and booking link-out */}
          <EventCallsToAction event={event} isAtCapacity={isAtCapacity} />

          {/* Reviews Section */}
          <EventReviews eventId={id || ''} eventName={event.name} includesFood={false} />

          <div className="pt-4 border-t border-gray-200">
            <ReportErrorLink entityType="event" entityId={event.id} entityName={event.name} />
          </div>
        </div>
      </PageContainer>

      {/* Image Modal */}
      <ImageModal
        images={getEventPhotos()}
        initialIndex={selectedImageIndex}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </div>
  );
};
